import { useRef, useState } from "react";
import { Link, useNavigate, useRouteLoaderData } from "react-router-dom";

import { ReactComponent as Download } from "src/img/svg/download.svg";
import { ReactComponent as Upload } from "src/img/svg/upload.svg";
import UserInfo from "src/components/common/userInfo";
import { useAuthStore, type User } from "src/components/main/authStore";
import { useUploadStore } from "src/components/main/uploadStore";
import { useUiStore } from "src/components/main/uiStore";
import {
    myAxios,
    scroll,
    potholingReportWriterEmails,
} from "src/components/common/functions";
import type { ReportRecord } from "./types";

const title = "Reports";

export default function Detail() {
    const { report } = useRouteLoaderData("Report") as { report: ReportRecord };
    const navigate = useNavigate();
    const setShowLoader = useUiStore(state => state.setShowLoader);
    const user = useAuthStore(state => state.user) as User;
    const upload = useUploadStore();
    const receiptUploadRef = useRef<HTMLInputElement | null>(null);
    const [comments, setComments] = useState(report.approverNotes || "");
    const [curPdfId, setCurPdfId] = useState(report.pdfId ?? "");
    const stats = [
        report.pothole ? `${report.pothole} Potholes` : "",
        report.ep ? `${report.ep} EPs` : "",
        report.manhole ? `${report.manhole} Manholes` : "",
    ].filter(Boolean).join(", ");
    const voidReport = async (id: string) => {
        if (!window.confirm("Are you sure to void this report?")) return;
        let voidReason = window.prompt("reason to void:");
        if (voidReason === null) return;
        while (voidReason === "") {
            window.alert("Reason is required to void.");
            voidReason = window.prompt("reason to void:");
            if (voidReason === null) return;
        }
        const formData = new FormData();
        formData.append("id", id);
        formData.append("voidReason", voidReason);
        const res = await myAxios({ fileName: "reports/voidReport.php", formData });
        if (!res) return;
        navigate(`/${title}`, { replace: true });
        scroll(null);
    };
    const validateReport = async (id: string) => {
        if (!window.confirm("Are you sure to validate this report?")) return;
        let validateReason = window.prompt("reason to validate:");
        if (validateReason === null) return;
        while (validateReason === "") {
            window.alert("Reason is required to validate.");
            validateReason = window.prompt("reason to validate:");
            if (validateReason === null) return;
        }
        const formData = new FormData();
        formData.append("id", id);
        formData.append("validateReason", validateReason);
        const res = await myAxios({ fileName: "reports/validateReport.php", formData });
        if (!res) return;
        navigate(`/${title}`, { replace: true });
        scroll(null);
    };

    const submitReport = async (id: string) => {
        if (!window.confirm("Are you sure to submit this report?")) return;
        const formData = new FormData();
        formData.append("id", id);
        const res = await myAxios({ fileName: "reports/submitReport.php", formData });
        if (!res) return;
        navigate(`/${title}`, { replace: true });
        scroll(null);
    };
    const withdrawReport = async (id: string) => {
        if (!window.confirm("Are you sure to withdraw this report?")) return;
        const formData = new FormData();
        formData.append("id", id);
        const res = await myAxios({ fileName: "reports/withdrawReport.php", formData });
        if (!res) return;
        navigate(`/${title}`, { replace: true });
        scroll(null);
    };
    const handleUpdatePDF = async (target: HTMLInputElement | DataTransfer) => {
        const files = target.files;
        if (!files?.length || !files[0]?.name) return;
        const file = files[0];
        if (curPdfId && !window.confirm("Are you sure to overwrite the existing report file?")) return;
        const formData = new FormData();
        formData.append("id", reportId);
        formData.append("fileName", file.name);
        formData.append("fileType", file.type);
        formData.append("fileSize", String(file.size));
        formData.append("lastModifiedAt", new Date(file.lastModified).toISOString().slice(0, 19).replace("T", " "));
        setShowLoader(true);
        const preRes = await myAxios({ fileName: "reports/createReportPDF.php", formData });
        setShowLoader(false);
        if (!preRes) return;
        upload.setUrls([preRes.data.url]);
        upload.setIds([preRes.data.id]);
        upload.setFiles([file]);
        upload.setShow(true);
        upload.setOnResetCallback(() => {
            setCurPdfId(preRes.data.id);
            if ("value" in target) target.value = "";
        });
    };
    const handleDownloadPDF = async () => {
        if (!curPdfId) return;
        const formData = new FormData();
        formData.append("id", reportId);
        setShowLoader(true);
        const res = await myAxios({ fileName: "reports/getReportPDF.php", formData });
        setShowLoader(false);
        if (!res) return;
        window.open(res.data, "_blank", "noopener,noreferrer");
    };
    const handleRemovePDF = async () => {
        if (!curPdfId) return;
        if (!window.confirm("Are you sure to remove this report file?")) return;
        const formData = new FormData();
        formData.append("id", reportId);
        setShowLoader(true);
        const res = await myAxios({ fileName: "reports/deleteReportPDF.php", formData });
        setShowLoader(false);
        if (!res) return;
        setCurPdfId("");
    };
    const reportId = String(report.id);

    return (
        <div className="border text-left" style={{ borderRadius: "6px" }}>
            <div className="table-responsive" style={{ minWidth: "800px", maxWidth: "90%", margin: "20px auto" }}>
                <table className="table">
                    <tbody>
                        <tr>
                            <th>Status</th>
                            <td><span children={report.status} className="badge text-bg-primary" /></td>
                        </tr>
                        <tr>
                            <th>Project</th>
                            <td>
                                <Link to={`/Projects/${report.projectId}`}>
                                    {report.projectName}
                                </Link>
                            </td>
                        </tr>
                        <tr>
                            <th>Report Dates</th>
                            <td>{report.startDate} - {report.endDate}</td>
                        </tr>
                        <tr>
                            <th>Stats</th>
                            <td>{stats}</td>
                        </tr>
                        <tr>
                            <th>Code</th>
                            <td>{report.code}</td>
                        </tr>
                        <tr>
                            <th>Priority</th>
                            <td>{report.priority}</td>
                        </tr>
                        <tr>
                            <th>Pending on Other Department</th>
                            <td>{report.pending}</td>
                        </tr>
                        <tr>
                            <th>Supplementary Report</th>
                            <td>{report.sup}</td>
                        </tr>
                        <tr>
                            <th>Report Tech</th>
                            <td><UserInfo name={report.reportTech ?? ""} id={report.reportTechId} /></td>
                        </tr>
                        <tr>
                            <th>Project Manager</th>
                            <td><UserInfo name={report.projectManager ?? ""} id={report.projectManagerId} /></td>
                        </tr>
                        {report.requestor || report.requestorId ? (
                        <tr>
                            <th>Requestor</th>
                            <td><UserInfo name={report.requestor ?? ""} id={report.requestorId} /></td>
                        </tr>
                        ) : null}
                        {report.notes ? (
                        <tr>
                            <th>Notes</th>
                            <td><span className="cssNotes">{report.notes}</span></td>
                        </tr>
                        ) : null}
                        <tr>
                            <th>Creator</th>
                            <th><UserInfo name={report.creatorName} id={report.creatorId} /> {report.createdAt}</th>
                        </tr>
                        {report.updaterId ? (
                        <tr>
                            <th>Updater</th>
                            <th><UserInfo name={report.updaterName ?? ""} id={report.updaterId} /> {report.updatedAt}</th>
                        </tr>
                        ) : null}
                        {(
                            <>
                                <tr>
                                    <th>Submit</th>
                                    <th><UserInfo name={report.creatorName ?? ""} id={report.creatorId} /> {report.createdAt}</th>
                                </tr>
                                <tr>
                                    <th>Approver</th>
                                    <th><UserInfo name={report.projectManager} id={report.projectManagerId} /> {report.decisionTime}</th>
                                </tr>
                                <tr>
                                    <th>Approver Notes</th>
                                    <th>{report.approverNotes}</th>
                                </tr>
                                {report.notify ? (
                                    <tr>
                                        <th>Notified At</th>
                                        <th>{report.notify}</th>
                                    </tr>
                                ) : null}
                            </>
                        )}
                        <tr>
                            <th>File</th>
                            <td
                                onDragOver={event => {
                                    event.stopPropagation();
                                    event.preventDefault();
                                }}
                                onDrop={event => {
                                    event.preventDefault();
                                    event.stopPropagation();
                                    void handleUpdatePDF(event.dataTransfer);
                                }}
                            >
                                <button
                                    type="button"
                                    className="mb-0 btn btn-primary"
                                    onClick={() => receiptUploadRef.current?.click()}
                                >
                                    <Upload/>
                                </button>
                                <input
                                    type="file"
                                    ref={receiptUploadRef}
                                    style={{ display: "none" }}
                                    onChange={event => void handleUpdatePDF(event.currentTarget)}
                                />
                                {curPdfId ? (
                                <>
                                    <button
                                        type="button"
                                        className="btn btn-success ms-3"
                                        onClick={handleDownloadPDF}
                                    >
                                        <Download/>
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-danger ms-3 text-white"
                                        onClick={() => void handleRemovePDF()}
                                    >
                                        &times;
                                    </button>
                                </>
                                ) : null}
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
            {report.void === "no" ? (
                <div className="mb-3 mx-3">
                    {report.status === "Submitted" ? (
                    <button
                        children="Withdraw"
                        type="button"
                        className="btn btn-warning"
                        onClick={() => withdrawReport(reportId)}
                    />
                    ) : (
                    <>
                    <Link
                        children="Edit"
                        to="Edit"
                        className="btn btn-outline-primary"
                    />
                    <button
                        children="Void"
                        type="button"
                        className="btn btn-danger ms-2"
                        onClick={() => voidReport(reportId)}
                    />
                    {curPdfId ? (
                    <button
                        children="Submit"
                        type="button"
                        className="btn btn-success ms-2"
                        onClick={() => submitReport(reportId)}
                    />
                    ) : null}
                    </>
                    )}
                    <Link
                        children="Back"
                        to=".."
                        relative="path"
                        className="btn btn-outline-secondary float-end"
                    />
                </div>
            ) : (
                <div className="mb-3 mx-3">
                    <button
                        children="Validate"
                        type="button"
                        className="btn btn-primary"
                        onClick={() => validateReport(reportId)}
                    />
                    <Link
                        children="Back"
                        to=".."
                        relative="path"
                        className="btn btn-outline-secondary float-end"
                    />
                </div>
            )}
            {(potholingReportWriterEmails as string[]).includes(report.reportTechId) ? (
                <>
                    {report.projectManagerId === user.email ? (
                        <div className="mb-4">
                            <div style={{ marginLeft: "5%", marginBottom: "10px" }}>Approval Notes</div>
                            <textarea
                                disabled={report.status !== "Submitted"}
                                className="form-control"
                                style={{ width: "90%", margin: "auto" }}
                                value={comments}
                                onChange={e => setComments(e.currentTarget.value)}
                                name="comments"
                            />
                            <div>
                                {report.status === "Submitted" ? [
                                    { title: "Approve", decision: "Approved", word: "approve", color: "btn-success" },
                                    { title: "Reject", decision: "Rejected", word: "reject", color: "btn-danger" },
                                ].map(decision => (
                                    <span
                                        key={decision.decision}
                                        className={`btn ${decision.color} mx-3 mt-3`}
                                        onClick={async () => {
                                            if (!window.confirm(`Are you sure to ${decision.word} this report?`)) return;
                                            const formData = new FormData();
                                            formData.append("id", String(report.id));
                                            formData.append("approverDecision", decision.decision);
                                            formData.append("approverNotes", comments);
                                            formData.append("start", report.startDate);
                                            formData.append("end", report.endDate);
                                            setShowLoader(true);
                                            const res = await myAxios({ fileName: "reports/approval.php", formData });
                                            setShowLoader(false);
                                            if (!res) return;
                                        }}
                                    >
                                        {decision.title}
                                    </span>
                                )) : null}
                            </div>
                        </div>
                    ) : (
                        <div className="mb-4">
                            <div style={{ marginLeft: "5%", marginBottom: "10px" }}>Approval Notes</div>
                            <textarea disabled className="form-control" style={{ width: "90%", margin: "auto" }} value={comments} name="comments" />
                        </div>
                    )}
                </>
            ) : null}
        </div>
    );
}
